import { useEffect, useRef } from "react";
import { useSupremeEditsPlayer } from "../../contexts/SupremeEditsContext";
import { usePreviewPlayer } from "../../contexts/PreviewPlayerContext";

/**
 * PlayerCoordinator - Manages player priority and auto-close behavior
 * 
 * Ensures only one player is active at a time:
 * - Supreme Edit Player (for release tracks)
 * - Preview Track Player (for preview/producer pack tracks)
 * 
 * Rule: Latest interaction wins. When one player starts, the other auto-stops and closes.
 */
const PlayerCoordinator = () => {
  const { 
    isPlaying: supremeIsPlaying, 
    currentTrack: supremeCurrentTrack,
    closePlayer: closeSupremePlayer 
  } = useSupremeEditsPlayer();
  
  const { 
    isPlaying: previewIsPlaying, 
    currentPreview,
    closePlayer: closePreviewPlayer 
  } = usePreviewPlayer();

  // Track the last active player to detect changes
  const lastSupremePlaying = useRef(false);
  const lastPreviewPlaying = useRef(false);
  const lastSupremeTrack = useRef(null);
  const lastPreviewTrack = useRef(null);

  useEffect(() => {
    // Check if Supreme Edits Player is now active (playing with a track)
    const supremeIsActive = supremeIsPlaying && supremeCurrentTrack;
    const supremeWasActive = lastSupremePlaying.current && lastSupremeTrack.current;
    
    // Check if Preview Player is now active (playing with a preview)
    const previewIsActive = previewIsPlaying && currentPreview;
    const previewWasActive = lastPreviewPlaying.current && lastPreviewTrack.current;

    // Detect if Supreme Edits Player just became active (new track or started playing)
    const supremeJustActivated = 
      supremeIsActive && 
      (!supremeWasActive || supremeCurrentTrack !== lastSupremeTrack.current);

    // Detect if Preview Player just became active (new preview or started playing)
    const previewJustActivated = 
      previewIsActive && 
      (!previewWasActive || currentPreview !== lastPreviewTrack.current);

    // If Supreme Edits Player activates, close Preview Player
    if (supremeJustActivated && (previewIsPlaying || currentPreview)) {
      console.log("Supreme Edits Player activated - closing Preview Player");
      closePreviewPlayer();
    }

    // If Preview Player activates, close Supreme Edits Player
    if (previewJustActivated && (supremeIsPlaying || supremeCurrentTrack)) {
      console.log("Preview Player activated - closing Supreme Edits Player");
      closeSupremePlayer();
    }

    // Update refs for next comparison
    lastSupremePlaying.current = supremeIsPlaying;
    lastPreviewPlaying.current = previewIsPlaying;
    lastSupremeTrack.current = supremeCurrentTrack;
    lastPreviewTrack.current = currentPreview;
  }, [
    supremeIsPlaying, 
    previewIsPlaying, 
    supremeCurrentTrack, 
    currentPreview,
    closeSupremePlayer,
    closePreviewPlayer
  ]);

  // This component doesn't render anything
  return null;
};

export default PlayerCoordinator;
